import { type FC, type ReactNode } from 'react';

import { Box, HStack, Icon, ListRow, Text, VStack } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';

import { GradientIconBadge } from '~/b4b/components/gradient-icon-badge/GradientIconBadge';
import { SambaAutoLink, type SambaAutoLinkProps } from '~/b4b/components/SambaAutoLink';

const unstyledLinkStyles = css.raw({
  color: 'inherit',
  width: '100%',
  _hover: {
    color: 'inherit',
    textDecoration: 'none',
    '& .underlineOnHover': { textDecoration: 'underline' },
  },
});

const listRowStyles = css.raw({
  p: 'space.0',
});

const leadingContentStyles = css.raw({
  pl: 'space.0',
  gap: 'space.25',
});

const trailingStyles = css.raw({
  pr: 'space.0',
});

const resourceSectionStyles = css.raw({
  alignItems: 'flex-start',
  pt: 'space.300',
  pb: 'space.300',
  gap: 'space.200',
});

const resourceSectionInnerBoxStyles = css.raw({
  flex: 1,
});

const labelStyles = css.raw({
  display: 'block',
});

const UnstyledSambaLink: FC<SambaAutoLinkProps> = ({ children, ...props }) => (
  <SambaAutoLink css={unstyledLinkStyles} {...props}>
    {children}
  </SambaAutoLink>
);

const RowTitle: FC<{ children: ReactNode }> = ({ children }) => (
  <Text className="underlineOnHover" variant="text100">
    {children}
  </Text>
);

RowTitle.displayName = 'ResourceCard.Row.Title';

const RowDescription: FC<{ children: ReactNode }> = ({ children }) => (
  <Text variant="text50" color="text.weak" className="description">
    {children}
  </Text>
);

RowDescription.displayName = 'ResourceCard.Row.Description';

type RowProps = {
  to: string;
  eventName: string;
  eventModule: string;
  children: ReactNode;
};

const RowBase: FC<RowProps> = ({ to, children, eventName, eventModule }) => (
  <UnstyledSambaLink
    to={to}
    target="_blank"
    rel="noopener noreferrer"
    data-track-event={eventName ? 'ElementClicked' : undefined}
    data-track-name={eventName}
    data-track-module={eventModule}
    data-track-flow={eventName ? 'ResourcesLanding' : undefined}
  >
    <ListRow css={listRowStyles}>
      <ListRow.LeadingContent css={leadingContentStyles}>{children}</ListRow.LeadingContent>
      <ListRow.TrailingAsset css={trailingStyles}>
        <Icon name="CaretRight" size="xsmall" />
      </ListRow.TrailingAsset>
    </ListRow>
  </UnstyledSambaLink>
);

const Row = Object.assign(RowBase, { Title: RowTitle, Description: RowDescription });

Row.displayName = 'ResourceCard.Row';

interface SectionProps {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}

const Section: FC<SectionProps> = ({ icon, label, children }) => (
  <HStack css={resourceSectionStyles}>
    <GradientIconBadge>{icon}</GradientIconBadge>
    <Box css={resourceSectionInnerBoxStyles}>
      <Text css={labelStyles} variant="text25" color="text.weak">
        {label}
      </Text>
      <VStack css={{ gap: 'space.200' }}>{children}</VStack>
    </Box>
  </HStack>
);

Section.displayName = 'ResourceCard.Section';

export const ResourceCard = { Section, Row };
